import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { LottiePlayer, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext } from '~/contexts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useIntegration } from '~/hooks/useIntegration';
import { useLocaleKeys } from '~/i18n';
import { accountingIntegration } from '~/models/integration/selectors';
import { Accounting } from '~/types/api.model';
import qboSyncAnimation from './qbo-sync.json';
import styles from './QboXeroIntegrationStep.module.scss';
import xeroSyncAnimation from './xero-sync.json';
export const GetQboXeroIntegrationStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const integrationConnectionStepTextsPath = 'vendorOnboarding.integrationConnectionStep';
export const QboXeroIntegrationStep = (param)=>{
    let { testId, accountingType, connected, onContinue, onConnectingChange, onConnectedChange, submitRef } = param;
    const testIds = useMemo(()=>GetQboXeroIntegrationStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(integrationConnectionStepTextsPath);
    const { browser: { changeLocation } } = useIoContext();
    const { connect } = useIntegration(accountingType);
    const { refresh: refreshIntegrationsSummary } = useIntegrationsSummary({
        enabled: false
    });
    const hasAdvancedRef = useRef(false);
    const isQbo = accountingType === Accounting.QBO;
    const animationData = isQbo ? qboSyncAnimation : xeroSyncAnimation;
    const connectedMessage = isQbo ? t.connectedQbo() : t.connectedXero();
    const advanceOnce = useCallback(()=>{
        if (hasAdvancedRef.current) return;
        hasAdvancedRef.current = true;
        onConnectedChange === null || onConnectedChange === void 0 ? void 0 : onConnectedChange(true);
        onContinue();
    }, [
        onConnectedChange,
        onContinue
    ]);
    useEffect(()=>{
        let cancelled = false;
        void (async ()=>{
            const { data: summary } = await refreshIntegrationsSummary({
                throwOnError: false
            });
            const integration = accountingIntegration(summary, accountingType);
            if (!cancelled && (integration === null || integration === void 0 ? void 0 : integration.isLinked())) {
                advanceOnce();
            }
        })();
        return ()=>{
            cancelled = true;
        };
    }, [
        accountingType,
        advanceOnce,
        refreshIntegrationsSummary
    ]);
    const handleConnect = useCallback(async ()=>{
        onConnectingChange === null || onConnectingChange === void 0 ? void 0 : onConnectingChange(true);
        const redirectUrl = "".concat(window.location.origin, "/home");
        const linkUrl = await connect(redirectUrl);
        if (linkUrl == null) {
            onConnectingChange === null || onConnectingChange === void 0 ? void 0 : onConnectingChange(false);
            return;
        }
        const connectUrl = new URL(linkUrl);
        connectUrl.searchParams.append('host', window.location.host);
        changeLocation(connectUrl.toString());
    }, [
        changeLocation,
        connect,
        onConnectingChange
    ]);
    const handleSubmit = useCallback(async ()=>{
        if (connected) {
            onContinue();
            return;
        }
        await handleConnect();
    }, [
        connected,
        handleConnect,
        onContinue
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: handleSubmit
        }), [
        handleSubmit
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.QboXeroIntegrationStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles.lottieContainer,
                children: /*#__PURE__*/ _jsx(LottiePlayer, {
                    animationData: animationData,
                    loop: true,
                    autoplay: true,
                    className: styles.lottie,
                    testId: testIds.QboXeroIntegrationStepTestIds.LottieAnimation()
                })
            }),
            connected && /*#__PURE__*/ _jsx("div", {
                "data-testid": testIds.QboXeroIntegrationStepTestIds.ConnectedText(),
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    className: styles.connected,
                    children: connectedMessage
                })
            })
        ]
    });
};
